// The checks on a compiled output that don't need the page layout (`Preflight`, SuitePreflight/Preflight.swift):
// the copyright page's ISBNs and stray placeholders, a market's rules, blind reading, the Word file's format, and
// the EPUB checks named by chapter. Print preflight (PDF layout) is W5b's: pass it in as `pdf`.
import { EDITIONS, editionLabel, ISBN, isbnOf, blockPlainText, type Edition } from '@writing-suite/core';
import { epubFiles } from './epub.ts';
import { epubCheck } from './epubCheck.ts';
import { formatted, isDocument, publicationEdition } from './manuscript.ts';
import { swiftDoubleDescription } from './presets.ts';
import { fileExtension, type CompiledManuscript, type CompilePreset, type OutputFormat } from './types.ts';
import { ZipReader } from './zip.ts';

export interface PreflightFinding {
  severity: 'error' | 'warning' | 'info';
  code: string;
  message: string;
  fragmentID?: string;
  /** 1-based page of the PDF, for layout findings. */
  page?: number;
  quote?: string;
  context?: string;
  copyfit?: string;
}

const finding = (severity: PreflightFinding['severity'], code: string, message: string, extra: Partial<PreflightFinding> = {}): PreflightFinding =>
  ({ severity, code, message, ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v !== undefined && v !== null)) });

const RANK = { error: 0, warning: 1, info: 2 } as const;

/** A market's submission rules (`MarketRules`); undefined always means "not recorded", never "not required". */
export interface MarketRules {
  name: string;
  length?: { unit: 'words' | 'pages' | 'chapters'; min?: number; max?: number };
  blindReading?: boolean;
  acceptedFileTypes?: string[];
  allowsSimultaneous?: boolean;
  formatRequirement?: string;
  hardNos?: string;
  aiPolicy?: string;
  guidelinesURL?: string;
  lastVerified?: Date;
  unverifiedFields: string[];
  /** False for rules the writer typed in (just a word limit). */
  reportsUnrecorded: boolean;
}

/** `MarketRules(name:minWords:maxWords:blind:)`: a limit the writer typed in. */
export function typedMarket(name: string, o: { minWords?: number; maxWords?: number; blind?: boolean } = {}): MarketRules {
  return {
    name, unverifiedFields: [], reportsUnrecorded: false,
    ...(o.minWords !== undefined || o.maxWords !== undefined ? { length: { unit: 'words' as const, ...(o.minWords !== undefined ? { min: o.minWords } : {}), ...(o.maxWords !== undefined ? { max: o.maxWords } : {}) } } : {}),
    ...(o.blind !== undefined ? { blindReading: o.blind } : {}),
  };
}

/** What compile made, for preflight (`CompileResult`'s data, format and manuscript). */
export interface CompiledOutput {
  data: Uint8Array;
  format: OutputFormat;
  manuscript: CompiledManuscript;
}

export interface PreflightOptions {
  /** Names to search for in a blind manuscript. */
  identity?: string[];
  market?: MarketRules | null;
  /** Print preflight for PDFs (W5b's `PrintPreflight.check`). */
  pdf?: (output: CompiledOutput) => PreflightFinding[];
  /** A PDF's page count, for a market that limits pages. */
  pdfPageCount?: (data: Uint8Array) => number | null;
}

/** `Preflight.check`. */
export function preflight(output: CompiledOutput, o: PreflightOptions = {}): PreflightFinding[] {
  const findings: PreflightFinding[] = [];
  const m = output.manuscript;
  const identity = o.identity ?? [];
  if (o.market && !isDocument(m)) findings.push(...marketChecks(m, output.format, output.data, o.market, o.pdfPageCount));
  findings.push(...publicationChecks(m));
  switch (output.format) {
    case 'docx': {
      let zip: ZipReader | null = null;
      try { zip = new ZipReader(output.data); } catch (e) {
        findings.push(finding('error', 'docx.unreadable', `The generated Word file could not be read back: ${(e as Error).message}`));
      }
      if (zip) {
        findings.push(...docxFormatChecks(zip, m.preset));
        if (m.preset.blind || o.market?.blindReading === true) findings.push(...blindChecks(zip, m, identity));
      }
      break;
    }
    case 'epub':
      findings.push(...epubChecks(output.data, m));
      break;
    case 'pdf':
      if (o.pdf) findings.push(...o.pdf(output));
      break;
    default:
      break;
  }
  return findings.sort((a, b) => RANK[a.severity] - RANK[b.severity] || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0) || (a.page ?? 0) - (b.page ?? 0));
}

/** `publicationChecks`: the copyright page's ISBNs, and placeholders left unfilled on any book page. */
export function publicationChecks(m: CompiledManuscript): PreflightFinding[] {
  const findings: PreflightFinding[] = [];
  const matter = m.sections.filter((s) => s.matter !== null);
  for (const section of matter) {
    const text = section.blocks.map(blockPlainText).join('\n');
    for (const match of text.matchAll(/\{[A-Za-z]+\}/g)) {
      findings.push(finding('warning', 'matter.placeholder',
        `“${match[0]}” on the ${section.title} page isn't a placeholder Five Rooms fills in, so it's printed as written.`,
        { fragmentID: section.fragmentID ?? undefined, quote: match[0] }));
    }
  }
  const copyright = matter.find((s) => s.matter === 'copyright');
  if (!copyright || m.preset.blind) return findings;
  const publication = m.publication;
  const at = { fragmentID: copyright.fragmentID ?? undefined };
  for (const edition of EDITIONS) {
    const isbn = isbnOf(publication, edition);
    const problem = ISBN.problem(isbn);
    if (problem) findings.push(finding('warning', 'publication.isbn', `The ${editionLabel(edition).toLowerCase()} ISBN ${isbn} doesn't check out. ${problem}`, at));
  }
  const seen = new Map<string, Edition>();
  for (const edition of EDITIONS) {
    const check = ISBN.check(isbnOf(publication, edition));
    if (check.type !== 'valid') continue;
    const other = seen.get(check.isbn13);
    if (other) {
      findings.push(finding('warning', 'publication.isbn-shared',
        `The ${editionLabel(other).toLowerCase()} and ${editionLabel(edition).toLowerCase()} have the same ISBN. Each edition needs its own.`, at));
    }
    seen.set(check.isbn13, edition);
  }
  const edition = publicationEdition(m.preset);
  if (edition !== null && edition !== 'audiobook' && isbnOf(publication, edition) === '') {
    findings.push(finding('info', 'publication.isbn-missing',
      `No ${editionLabel(edition).toLowerCase()} ISBN, so the copyright page has none for this edition. Add it in Compile ▸ Publication Details, or leave it out if your printer or store assigns one.`, at));
  }
  return findings;
}

function range(length: NonNullable<MarketRules['length']>): string {
  const unit = length.unit;
  if (length.min !== undefined && length.max !== undefined) return `range of ${formatted(length.min)}–${formatted(length.max)} ${unit}`;
  if (length.max !== undefined) return `limit of ${formatted(length.max)} ${unit}`;
  if (length.min !== undefined) return `minimum of ${formatted(length.min)} ${unit}`;
  return 'length';
}

/** `marketChecks`. */
export function marketChecks(m: CompiledManuscript, format: OutputFormat, data: Uint8Array, market: MarketRules,
  pdfPageCount?: (data: Uint8Array) => number | null): PreflightFinding[] {
  const f: PreflightFinding[] = [];
  const name = market.name;
  const length = market.length;
  if (length && (length.min !== undefined || length.max !== undefined)) {
    let measured: number | null;
    switch (length.unit) {
      case 'words': measured = m.wordCount; break;
      case 'chapters': measured = m.sections.filter((s) => s.role === 'chapter').length; break;
      case 'pages': measured = format === 'pdf' && pdfPageCount ? pdfPageCount(data) : null; break;
    }
    const unit = length.unit;
    if (measured !== null) {
      if (length.max !== undefined && measured > length.max) {
        f.push(finding('error', 'market.too-long', `${formatted(measured)} ${unit} is over ${name}'s limit of ${formatted(length.max)} by ${formatted(measured - length.max)}.`));
      } else if (length.min !== undefined && measured < length.min) {
        f.push(finding('error', 'market.too-short', `${formatted(measured)} ${unit} is under ${name}'s minimum of ${formatted(length.min)}.`));
      } else {
        f.push(finding('info', 'market.length-ok', `${formatted(measured)} ${unit} is within ${name}'s ${range(length)}.`));
      }
    } else {
      f.push(finding('warning', 'market.pages-unchecked', `${name} limits length to ${range(length)}; page counts can only be checked on a PDF compile.`));
    }
  } else if (market.reportsUnrecorded) {
    f.push(finding('warning', 'market.length-unknown', `${name} hasn't recorded a length limit. Check the guidelines.`));
  }
  if (market.acceptedFileTypes && market.acceptedFileTypes.length) {
    const accepted = market.acceptedFileTypes.map((t) => t.toLowerCase().replace(/^[. ]+|[. ]+$/g, ''));
    if (!accepted.includes(fileExtension(format).toLowerCase())) {
      f.push(finding('error', 'market.format', `${name} accepts ${accepted.join(', ')}, not ${fileExtension(format)}.`));
    }
  } else if (market.reportsUnrecorded) {
    f.push(finding('warning', 'market.format-unknown', `${name} hasn't recorded which file types it accepts.`));
  }
  if (market.blindReading === true) {
    if (!m.preset.blind) f.push(finding('error', 'market.blind', `${name} reads blind; compile with a blind preset.`));
  } else if (market.blindReading === undefined && !m.preset.blind && market.reportsUnrecorded) {
    f.push(finding('warning', 'market.blind-unknown', `It isn't recorded whether ${name} reads blind. Check before sending a manuscript with your name on it.`));
  }
  if (market.formatRequirement) f.push(finding('info', 'market.format-requirement', `${name} asks for: ${market.formatRequirement}`));
  if (market.unverifiedFields.length) {
    f.push(finding('warning', 'market.unverified', `These ${name} rules were filled in automatically and haven't been verified: ${market.unverifiedFields.join(', ')}.`));
  }
  return f;
}

// MARK: Blind reading

const containsFolded = (text: string, name: string) => text.toLowerCase().includes(name.toLowerCase());

/** `blindChecks`: the author's names in properties, headers, title page or text. */
export function blindChecks(zip: ZipReader, m: CompiledManuscript, identity: string[]): PreflightFinding[] {
  const f: PreflightFinding[] = [];
  const names = identity.filter((n) => [...n].length >= 3);
  const core = zip.text('docProps/core.xml');
  if (core !== null) {
    for (const tag of ['dc:creator', 'cp:lastModifiedBy']) {
      const value = xmlValue(core, tag);
      if (value) f.push(finding('error', 'blind.properties', `Document property ${tag} contains “${value}”.`));
    }
  }
  const app = zip.text('docProps/app.xml');
  const company = app !== null ? xmlValue(app, 'Company') : null;
  if (company) f.push(finding('error', 'blind.properties', `Document property Company contains “${company}”.`));
  const headers = [...zip.entries.keys()].filter((k) => k.startsWith('word/header') || k.startsWith('word/footer')).sort();
  for (const part of headers) {
    const text = visibleText(zip.text(part) ?? '');
    for (const name of names) if (containsFolded(text, name)) f.push(finding('error', 'blind.header', `The running header contains “${name}”.`));
  }
  const body = visibleText(zip.text('word/document.xml') ?? '');
  for (const name of names) {
    if (!containsFolded(body, name)) continue;
    const sources = m.sections.filter((s) => s.blocks.some((b) => containsFolded(blockPlainText(b), name)));
    if (!sources.length) f.push(finding('error', 'blind.title-page', `“${name}” appears on the title page or in a heading.`));
    for (const s of sources) f.push(finding('error', 'blind.body', `“${name}” appears in the text of “${s.title}”.`, { fragmentID: s.fragmentID ?? undefined }));
  }
  if (!f.length) {
    f.push(finding('info', 'blind.ok', `No author identity found in properties, headers, title page or text${names.length ? '' : ' (no names to search for were given)'}.`));
  }
  return f;
}

// MARK: Format conformance

/** `docxFormatChecks`: the Word file carries the preset's font, size, spacing, margins and header. */
export function docxFormatChecks(zip: ZipReader, preset: CompilePreset): PreflightFinding[] {
  const f: PreflightFinding[] = [];
  const r = preset.render;
  const styles = zip.text('word/styles.xml'), document = zip.text('word/document.xml');
  if (styles === null || document === null) return [finding('error', 'docx.parts', 'The Word file is missing its styles or document part.')];
  const num = (s: string | null) => (s !== null && s.trim() !== '' && Number.isFinite(Number(s)) ? Number(s) : null);
  const size = num(attribute(styles, 'w:sz', 'w:val'));
  if (size !== null && size / 2 !== r.font.size) {
    f.push(finding('error', 'format.font-size', `Body text is ${swiftDoubleDescription(size / 2)}pt; the preset requires ${swiftDoubleDescription(r.font.size)}pt.`));
  }
  const font = attribute(styles, 'w:rFonts', 'w:ascii');
  if (font !== null && font !== r.font.family) f.push(finding('error', 'format.font', `Body font is ${font}; the preset requires ${r.font.family}.`));
  const line = num(attribute(styles, 'w:spacing', 'w:line'));
  if (line !== null && Math.abs(line / 240 - r.lineSpacing) > 0.01) {
    f.push(finding('error', 'format.spacing', `Line spacing is ${swiftDoubleDescription(line / 240)}; the preset requires ${swiftDoubleDescription(r.lineSpacing)}.`));
  }
  const top = num(attribute(document, 'w:pgMar', 'w:top'));
  if (top !== null && Math.abs(top / 1440 - r.page.margins) > 0.01) {
    f.push(finding('error', 'format.margins', `Margins are ${swiftDoubleDescription(top / 1440)}in; the preset requires ${swiftDoubleDescription(r.page.margins)}in.`));
  }
  if (r.header !== '' && !zip.entries.has('word/header1.xml')) f.push(finding('error', 'format.header', 'The running header is missing.'));
  return f;
}

// MARK: EPUB

/** `epubChecks`: the native EPUB checks, each finding named by the chapter it's in and linked to its first document. */
export function epubChecks(data: Uint8Array, manuscript?: CompiledManuscript | null): PreflightFinding[] {
  const files = new Map<string, { title: string; fragmentIDs: string[] }>();
  if (manuscript) for (const f of epubFiles(manuscript)) if (!files.has(f.file)) files.set(f.file, { title: f.title, fragmentIDs: f.fragmentIDs });
  const findings = epubCheck(data).map((e) => {
    const source = e.file !== undefined ? files.get(e.file) : undefined;
    const place = source ? `In “${source.title}”: ` : e.file !== undefined ? `In ${e.file}${e.line !== undefined ? `, line ${e.line}` : ''}: ` : '';
    return finding(e.severity === 'error' ? 'error' : 'warning', `epub.${e.code}`, place + e.message, { fragmentID: source?.fragmentIDs[0] });
  });
  if (findings.some((f) => f.severity === 'error')) return findings;
  return [...findings, finding('info', 'epub.ok', "The EPUB passed this app's EPUB checks (container, package, navigation, XHTML, links and roles, modelled on epubcheck). "
    + "For a store's own validation, run epubcheck on the file, or `suite epubcheck` where it's installed.")];
}

// MARK: XML helpers

export function xmlValue(xml: string, tag: string): string | null {
  const open = xml.indexOf(`<${tag}`);
  if (open < 0) return null;
  const afterName = open + tag.length + 1;
  const start = xml.indexOf('>', afterName);
  if (start < 0) return null;
  if (xml.slice(afterName, start).endsWith('/')) return '';
  const close = xml.indexOf(`</${tag}>`, start + 1);
  if (close < 0) return null;
  return xml.slice(start + 1, close).trim();
}

const reEscape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function attribute(xml: string, element: string, name: string): string | null {
  const m = new RegExp(`<${reEscape(element)}\\b[^>]*\\b${reEscape(name)}="([^"]*)"`).exec(xml);
  return m ? m[1] : null;
}

/** Text content of WordprocessingML, from its `w:t` runs. */
export function visibleText(xml: string): string {
  return [...xml.matchAll(/<w:t(?: [^>]*)?>([^<]*)<\/w:t>/g)].map((m) => m[1]).join('');
}
